<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
/* eslint-disable @intlify/vue-i18n/no-raw-text */
/* eslint-disable no-alert */
import { onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import ProviderTeamsAPI from 'dashboard/api/providerTeams';
import Button from 'dashboard/components-next/button/Button.vue';
import SectionLayout from '../account/components/SectionLayout.vue';

const isLoading = ref(false);
const isInviting = ref(false);
const changingMemberId = ref(null);
const members = ref([]);
const providerName = ref('');

const form = reactive({
  name: '',
  email: '',
});

const loadTeam = async () => {
  isLoading.value = true;

  try {
    const { data } = await ProviderTeamsAPI.getTeam();
    providerName.value = data.payload?.provider?.name || '';
    members.value = data.payload?.members || [];
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'No se pudo cargar el equipo del proveedor.'
    );
  } finally {
    isLoading.value = false;
  }
};

const inviteMember = async () => {
  if (!form.name.trim() || !form.email.trim()) {
    useAlert('Indica el nombre y el correo electrónico.');
    return;
  }

  isInviting.value = true;

  try {
    await ProviderTeamsAPI.inviteMember({
      name: form.name.trim(),
      email: form.email.trim(),
    });

    form.name = '';
    form.email = '';

    await loadTeam();
    useAlert('Invitación enviada y perfil secundario activado.');
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'No se pudo crear el perfil secundario.'
    );
  } finally {
    isInviting.value = false;
  }
};

const setMemberActive = async (member, active) => {
  changingMemberId.value = member.user_id;

  try {
    await ProviderTeamsAPI.updateMember(member.user_id, active);
    await loadTeam();

    useAlert(
      active ? 'Perfil secundario activado.' : 'Perfil secundario desactivado.'
    );
  } catch (error) {
    useAlert(error.response?.data?.error || 'No se pudo actualizar el perfil.');
  } finally {
    changingMemberId.value = null;
  }
};

const removeMember = async member => {
  const confirmed = window.confirm(`¿Desvincular a ${member.name} del equipo?`);

  if (!confirmed) return;

  changingMemberId.value = member.user_id;

  try {
    await ProviderTeamsAPI.removeMember(member.user_id);
    await loadTeam();
    useAlert('Perfil secundario desvinculado.');
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'No se pudo desvincular el perfil.'
    );
  } finally {
    changingMemberId.value = null;
  }
};

onMounted(loadTeam);
</script>

<template>
  <SectionLayout
    with-border
    title="Mi equipo"
    :description="`Gestiona quién puede atender contigo los pedidos de ${providerName || 'tu proveedor'}.`"
  >
    <div class="flex flex-col w-full gap-5">
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          Nombre de la empleada
          <input
            v-model="form.name"
            type="text"
            class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
            placeholder="Nombre completo"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          Correo electrónico
          <input
            v-model="form.email"
            type="email"
            class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
            placeholder="empleada@correo.com"
          />
        </label>
      </div>

      <div>
        <Button
          label="Invitar perfil secundario"
          color="blue"
          :is-loading="isInviting"
          @click="inviteMember"
        />
      </div>

      <div v-if="isLoading" class="py-4 text-sm text-center text-n-slate-11">
        Cargando equipo...
      </div>

      <div
        v-else-if="!members.length"
        class="p-4 text-sm border rounded-xl border-n-weak text-n-slate-11"
      >
        Todavía no hay perfiles secundarios.
      </div>

      <div v-else class="flex flex-col divide-y divide-n-weak">
        <div
          v-for="member in members"
          :key="member.user_id"
          class="flex flex-wrap items-center justify-between gap-4 py-4"
        >
          <div>
            <div class="font-medium text-n-slate-12">
              {{ member.name }}
            </div>
            <div class="text-sm text-n-slate-11">
              {{ member.email }}
            </div>
            <div class="mt-1 text-xs text-n-slate-10">
              {{
                member.confirmed
                  ? member.active
                    ? 'Cuenta confirmada · Acceso activo'
                    : 'Cuenta confirmada · Acceso desactivado'
                  : 'Invitación pendiente de confirmar'
              }}
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            <Button
              v-if="member.active"
              label="Desactivar"
              size="sm"
              variant="ghost"
              color="slate"
              :is-loading="changingMemberId === member.user_id"
              @click="setMemberActive(member, false)"
            />
            <Button
              v-else
              label="Activar"
              size="sm"
              color="blue"
              :is-loading="changingMemberId === member.user_id"
              @click="setMemberActive(member, true)"
            />
            <Button
              label="Desvincular"
              size="sm"
              variant="ghost"
              color="slate"
              :is-loading="changingMemberId === member.user_id"
              @click="removeMember(member)"
            />
          </div>
        </div>
      </div>

      <p class="text-xs text-n-slate-10">
        Los pedidos, catálogos, ventas y comisiones continúan perteneciendo al
        proveedor principal.
      </p>
    </div>
  </SectionLayout>
</template>
